import { useAuth } from "@clerk/clerk-react";
import { Box, ButtonBase, IconButton, Typography } from "@mui/material";
import { getMarkRange } from "@tiptap/core";
import { Editor as TipTapEditor } from "@tiptap/react";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useState } from "react";

import { OrganizationTemplateRubricSuggestion } from "features/Influencer/ContactList/schema";
import { fetcherAuth } from "utils/api";

import styles from "./styles";

interface MergeTagSuggestionPopupProps {
  editor: TipTapEditor;
  suggestion: OrganizationTemplateRubricSuggestion;
  onApplied?: (suggestionId: number, beforeHtml: string) => void;
  onDismiss: () => void | Promise<void>;
}

const MergeTagSuggestionPopup = ({
  editor,
  suggestion,
  onApplied,
  onDismiss,
}: MergeTagSuggestionPopupProps) => {
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const [whyOpen, setWhyOpen] = useState(false);

  const tagToken =
    (suggestion.proposedReplacement ?? "").trim() || "{Brand Name}";
  const isFirstName = /\{[^}]*first[^}]*\}/i.test(tagToken);
  const tagHtml = toMergeTagHtml(tagToken);

  const handleApply = async (): Promise<void> => {
    if (!currentOrg?.id) return;

    const highlightMark = editor.state.schema.marks.highlight;
    if (!highlightMark) return;
    const { from } = editor.state.selection;
    const $pos = editor.state.doc.resolve(from);
    const range = getMarkRange($pos, highlightMark);
    if (!range) return;

    const beforeHtml = editor.getHTML();

    editor
      .chain()
      .focus()
      .setTextSelection({ from: range.from, to: range.to })
      .deleteSelection()
      .insertContent(tagHtml)
      .run();

    await fetcherAuth(
      getToken,
      `/api/organization/${currentOrg.id}/organization-template-rubric-suggestions/suggestion/${suggestion.id}`,
      "PATCH",
      {},
      { status: "applied" },
    );

    onApplied?.(suggestion.id, beforeHtml);
  };

  const insertCopy = isFirstName
    ? `On send, Bento replaces ${tagToken} with your contact's first name — so one template greets every person you reach out to correctly.`
    : `On send, Bento replaces ${tagToken} with whoever you're emailing — so one template works for ${suggestion.anchorText}, Drunk Elephant, Rhode, and every brand after.`;

  return (
    <Box sx={styles.container}>
      <Box sx={styles.head}>
        <Box sx={styles.avatar}>
          <Box
            component="i"
            className="fa-solid fa-tag"
            sx={styles.avatarIcon}
          />
        </Box>
        <Box sx={styles.helperBlock}>
          <Typography sx={styles.helperText}>Bento helper</Typography>
          <Box sx={styles.kindRow}>
            <Box component="span" sx={styles.kindDotError} />
            Error · Personalization tag
          </Box>
        </Box>
        <IconButton size="small" sx={styles.closeButton} onClick={onDismiss}>
          <Box
            component="i"
            className="fa-regular fa-xmark"
            sx={styles.closeIcon}
          />
        </IconButton>
      </Box>

      <Box sx={styles.body}>
        <Typography sx={styles.title}>
          Did you mean to use a personalization tag?
        </Typography>
        <Typography sx={styles.rationale}>{suggestion.body}</Typography>

        <Box sx={styles.diffCard}>
          <Box sx={styles.diffRow}>
            <Box component="span" sx={styles.diffMarkerDel}>
              You wrote
            </Box>
            <Box component="span" sx={styles.diffTextDel}>
              {suggestion.anchorText}
            </Box>
          </Box>
          <Box sx={styles.diffRow}>
            <Box component="span" sx={styles.diffMarkerAdd}>
              Use
            </Box>
            <Box component="span" sx={styles.diffTextAdd}>
              {tagToken}
            </Box>
          </Box>
        </Box>

        <Box sx={styles.infoBox}>
          <Box
            component="i"
            className="fa-solid fa-circle-info"
            sx={styles.infoBoxIcon}
          />
          <Box>{insertCopy}</Box>
        </Box>
      </Box>

      {whyOpen && (
        <Box sx={styles.whyBlock}>
          <Typography sx={styles.whyHead}>Why this matters</Typography>A literal
          brand name left in a template is a common cause of "Hi, I love working
          with [wrong brand]" mistakes. Personalization tags make a template
          safe to reuse across your whole outreach list — Bento substitutes the
          real brand on send.
        </Box>
      )}

      <Box sx={styles.actions}>
        <ButtonBase sx={styles.primaryButton} onClick={handleApply}>
          <Box
            component="i"
            className="fa-solid fa-check"
            sx={styles.primaryIcon}
          />
          Use personalization tag
        </ButtonBase>
        <ButtonBase sx={styles.rejectButton} onClick={onDismiss}>
          <Box
            component="i"
            className="fa-regular fa-xmark"
            sx={styles.rejectIcon}
          />
        </ButtonBase>
        <ButtonBase
          sx={styles.whyButton}
          onClick={() => setWhyOpen((prev) => !prev)}
        >
          <Box
            component="i"
            className="fa-regular fa-circle-info"
            sx={styles.whyIcon}
          />
          {whyOpen ? "Hide" : "Why?"}
        </ButtonBase>
      </Box>
    </Box>
  );
};

function toMergeTagHtml(text: string): string {
  return text.replace(/\{([^}]+)\}/g, (_, rawLabel) => {
    const label = rawLabel.trim();
    const kind = /first|brand/i.test(label) ? "merge-tag" : "variable";
    return `<span data-type="${kind}" class="${kind}" data-label="${label}">{${label}}</span>`;
  });
}

export default MergeTagSuggestionPopup;
